import {Box, Divider, Stack, Switch} from "@mui/material";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import {useTranslation} from "react-i18next";
import {useEntity} from "crqlar-react-common/dist/components/useEntity";
import SettingsEntityHeader from "../../layout/SettingsEntityHeader";
import DangerZone from "crqlar-react-common/dist/components/DangerZone";
import EmptyState from "crqlar-react-common/dist/components/EmptyState";
import Property from "crqlar-react-common/dist/components/Property";
import {useTrackScreenVisit} from "crqlar-react-common/dist/utils/analytics.util";
import {StripeConfigService} from "crqlar-react-common/dist/services/integrations/stripe/stripeConfig.service";
import {StripeConfig} from "crqlar-react-common/dist/models/integrations/stripe/stripeConfig.model";
import Button from "@mui/material/Button";
import ElectricalServicesIcon from "@mui/icons-material/ElectricalServices";
import {useNavigate} from "react-router-dom";

const StripeConfigurationDetails = () => {

    useTrackScreenVisit('stripe configuration details')

    //region State

    const {t} = useTranslation()
    const navigate = useNavigate()

    const stripeConfigData = useEntity({
        get: async () => {
            return await StripeConfigService.get()
        },
        update: StripeConfigService.save,
        create: StripeConfigService.save,
        delete: (id) => StripeConfigService.delete({id: [id]}),
    })

    const config = stripeConfigData.item
    const setConfig = stripeConfigData.setItem


    //endregion Sate

    //region Handlers

    const updateField = (field: keyof StripeConfig, value: any) => {
        setConfig({...config, [field]: value})
    }

    //endregion Handlers

    //region UI

    const headerSection = (
        <SettingsEntityHeader
            title={t('Stripe account')}
            data={stripeConfigData}
            back={() => navigate('/system')}
        />
    )

    const emptyState = (
        <EmptyState
            icon={<ElectricalServicesIcon/>}
            title={t('No integration configured')}
            description={t('Create the configuration to connect this integration.')}
        >
            <Button variant={'contained'} onClick={() => stripeConfigData.startCreate()}>
                {t('Create')}
            </Button>
        </EmptyState>
    )

    const basicInfoSection = (
        <Stack gap={2}>
            <Typography variant={'h5'} textAlign={'start'}>
                {t('Basic info')}
            </Typography>

            <Stack gap={1}>
                <Property
                    title={t('Stripe connect id')}
                    tooltip={t('Connected Stripe account identifier used for payment routing.')}
                >
                    <TextField
                        id={'name'}
                        size={'small'}
                        value={config.stripeConnectId || ''}
                        onChange={(e) => updateField('stripeConnectId', e.target.value)}
                        fullWidth
                        required
                    />
                </Property>

                <Property
                    title={t('Enabled')}
                    tooltip={t('Turns Stripe payment processing on or off for this organization.')}
                >
                    <Switch
                        id={'enabled'}
                        checked={config.enabled}
                        onChange={(e) => updateField('enabled', e.target.checked)}
                    />
                </Property>
            </Stack>
        </Stack>
    )


    const dangerZoneSection = (
        <Stack gap={{xs: 2.5, sm: 2}}>
            <DangerZone
                title={t('Delete integration')}
                description={t('This permanently deletes the integration configuration and stops its syncing immediately. This action cannot be undone.')}
                buttonLabel={t('Delete')}
                loading={stripeConfigData.saving}
                onDelete={() => stripeConfigData.removeNow()}
                reloadOnDelete
            />

            <Typography variant={'body2'} color={'text.secondary'} textAlign={'start'}>
                {t('Last updated')}: {config.updateDate?.toLocaleString() || '-'}
            </Typography>
        </Stack>
    )

    //endregion UI

    const dataUI = (
        <Stack
            sx={{
                width: '100%',
                maxWidth: '900px',
                mx: 'auto',
                mt: 3,
                gap: 6,
            }}
        >
            {
                stripeConfigData.isMissing ? emptyState : (
                    <>
                        {basicInfoSection}
                    </>
                )
            }

            {
                !!config.id &&
                <>
                    <Divider/>
                    {dangerZoneSection}
                </>
            }
        </Stack>
    )

    return (
        <Box sx={{width: '100%', pb: 8}}>
            {headerSection}
            {dataUI}
        </Box>
    );
}

export default StripeConfigurationDetails;
